/**
 * Sign in with Apple JS loader for the composer.
 *
 * Twin of googleIdentity.js, and deliberately the same shape: one script loaded
 * once, one call that hands the caller a credential the composer never inspects.
 * The differences below are Apple's, not stylistic.
 *
 * WHY POPUP MODE. `usePopup: true` makes `AppleID.auth.signIn()` resolve with the
 * credential in-page. The alternative — Apple's default `response_mode=form_post`
 * redirect — makes Apple POST the result to our *backend*, which would then have
 * to bounce the browser back with a one-time code, i.e. the whole EdConnect
 * round trip built a second time for no gain. Popup keeps Apple's shape
 * identical to Google's: token in, token straight to the backend.
 *
 * WHY THE NONCE IS GENERATED HERE. Apple echoes it into the id_token, and the
 * backend compares it against the value we post alongside. That is what proves
 * the token was minted for THIS sign-in and is not a replay of a captured one.
 * It must therefore be fresh per attempt and never reused.
 *
 * WHERE THIS CANNOT WORK. Apple validates the page's domain against the Services
 * ID's registered "Domains and Subdomains" list, and refuses `localhost`
 * outright. So unlike the Google button, this one cannot be exercised from the
 * dev server — it only works on the deployed origin. `AppleID.auth.signIn()`
 * rejects with `invalid_client` when the domain is not registered.
 */
import Config from '../Config.jsx';

let appleJsPromise = null;

/** Apple serves a per-locale bundle; the path segment IS the locale. */
function localePath(locale) {
  return locale === 'tc' ? 'zh_TW' : 'en_US';
}

export function loadAppleJs(locale) {
  if (appleJsPromise) {
    return appleJsPromise;
  }
  appleJsPromise = new Promise((resolve, reject) => {
    if (window.AppleID && window.AppleID.auth) {
      resolve();
      return;
    }
    const existing = document.getElementById('apple-signin-script');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', (e) => reject(e));
      return;
    }
    const script = document.createElement('script');
    script.id = 'apple-signin-script';
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/'
      + localePath(locale) + '/appleid.auth.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = (e) => {
      appleJsPromise = null; // allow a retry after a network failure
      reject(e);
    };
    document.body.appendChild(script);
  });
  return appleJsPromise;
}

/**
 * A fresh random nonce, hex-encoded.
 *
 * crypto.getRandomValues, not Math.random: this value is the anti-replay bond
 * between our request and Apple's token, so a predictable one is the same as
 * none. Every browser the composer supports has it — there is no fallback on
 * purpose, because a silent downgrade to a guessable nonce is worse than a
 * button that fails loudly.
 */
export function newNonce() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * An Error carrying Apple's `error` code, so our own failures and the SDK's
 * (which rejects with a bare `{error: '...'}`) read the same at the call site.
 */
function appleError(code) {
  const e = new Error('apple: ' + code);
  e.error = code;
  return e;
}

/**
 * Open Apple's sign-in popup and resolve with `{ identityToken, rawNonce }`.
 *
 * Rejects with an error carrying `.error = '<apple code>'`. The one the user
 * causes constantly is `popup_closed_by_user` — callers must treat that as
 * "nothing happened", NOT as a refused sign-in, or closing the sheet by
 * accident shows a scary error.
 */
export async function signInWithApple(options = {}) {
  const clientId = Config.APPLE_SERVICES_ID();
  if (!clientId) {
    // Reached only if a caller skipped the ENABLE_APPLE_LOGIN gate. Failing
    // here beats letting Apple render "invalid_client" at the user.
    throw appleError('not_configured');
  }
  await loadAppleJs(options.locale);

  const rawNonce = newNonce();
  window.AppleID.auth.init({
    clientId: clientId,
    // The composer only ever needs the subject, and the backend resolves
    // link-only — it never reads name or email from an Apple token. Asking for
    // them anyway would show the user a consent screen for data we discard.
    scope: '',
    // Unused in popup mode for navigation, but Apple still validates it against
    // the Services ID's registered Return URLs and refuses a mismatch.
    redirectURI: Config.APPLE_REDIRECT_URI(),
    nonce: rawNonce,
    usePopup: true,
  });

  const data = await window.AppleID.auth.signIn();
  const identityToken = data
    && data.authorization
    && data.authorization.id_token;
  if (!identityToken) {
    throw appleError('no_identity_token');
  }
  // rawNonce travels to the backend UNHASHED. Apple's web flow echoes the value
  // verbatim into the token's `nonce` claim (the sha256 convention is the native
  // SDKs'), and the server's comparison accepts either form — so this is correct
  // for both and cannot silently pass the wrong one.
  return { identityToken: identityToken, rawNonce: rawNonce };
}

/** True when the user simply dismissed Apple's sheet — not an error to report. */
export function isUserCancelled(e) {
  const code = e && (e.error || (e.detail && e.detail.error));
  return code === 'popup_closed_by_user' || code === 'user_cancelled_authorize';
}
